import type { OptionDB } from "@project/emerge";
import type { Sides } from "@project/emerge-types";
import { Alignment } from "../enum/Alignment";
import { ConnectorCategory } from "../enum/ConnectorType";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import { VariableOptionType } from "../enum/VariableOptionType";
import type { ConnectorInfos } from "../interfaces/ConnectorInfos";
import type { ConnectorSetInfos } from "../interfaces/ConnectorSetInfos";
import type { HingeType } from "../interfaces/HingeType";
import type { LockType } from "../interfaces/LockType";
import { getOpposingSide } from "../user_options/Side";
import { calcPosition } from "./AddableInfos";
import { createVariableOption } from "./createVariableOption";

interface ConnectorSetOptions {
  alignment?: Alignment;
  count?: number;
  flip?: boolean;
  margin?: number;
  mirror?: boolean;
  pianoHinge?: boolean;
  pointSymmetry?: boolean;
}

export const createConnectorSet = (
  optionDb: OptionDB,
  connectorType: ConnectorCategory,
  group: string,
  side: Sides,
  part: string,
  {
    alignment = Alignment.CENTER,
    count = 2,
    flip = false,
    margin = alignment === Alignment.CENTER ? 0 : 80,
    mirror = false,
    pianoHinge = false,
    pointSymmetry = false,
  }: ConnectorSetOptions = {},
): ConnectorSetInfos => {
  return createVariableOption<ConnectorSetInfos>(
    connectorType === ConnectorCategory.Lock
      ? VariableOptionType.LOCK_SET
      : VariableOptionType.HINGE_SET,
    {
      alignment,
      connectorType,
      count,
      flip,
      group,
      margin,
      mirror,
      partSelection: optionDb.getByName<LockType | HingeType>(
        connectorType === ConnectorCategory.Lock
          ? LTCaseUserOption.LockType
          : LTCaseUserOption.HingeType,
        part,
      ),
      pianoHinge,
      pointSymmetry,
      side,
    },
  );
};
export const compileConnectorSet = (
  infos: ConnectorSetInfos,
): ConnectorInfos[] => {
  const {
    alignment,
    count,
    flip,
    group,
    margin,
    mirror,
    partSelection,
    pointSymmetry,
    side,
    connectorType,
    uuid,
  } = infos;
  // For hinges, pianoHinge comes from the selected Scharnier option (partSelection);
  // fall back to set-level infos.pianoHinge for backwards compatibility (e.g. import).
  const pianoHinge =
    connectorType === ConnectorCategory.Hinge
      ? ((partSelection as HingeType)?.pianoHinge ?? infos.pianoHinge)
      : false;

  const result: ConnectorInfos[] = [];
  for (const side2 of mirror ? [side, getOpposingSide(side)] : [side]) {
    const onOppositeSide = side2 !== side;
    for (let n = 0; n < count; n++) {
      result.push({
        connectorType,
        flip,
        group,
        partSelection,
        pianoHinge,
        // Emitted unflipped: whether the copy has to flip depends on the
        // orientation of the edge it lands on, which only the edge knows.
        // Edge.addConnector applies the flip.
        position: calcPosition(alignment, count, margin, n, false),
        side: side2,
        sourceOptionUuid: uuid,
        onOppositeSide,
        pointSymmetry: pointSymmetry === true,
      });
    }
  }
  return result;
};
